import { Phone, MessageCircle, Mail, AlertTriangle } from "lucide-react";
import { brandShort } from "../context/CompanyContext";

/**
 * BookingContactBadge — large, attention-grabbing red banner shown
 * after a client makes a reservation. It nudges them to confirm
 * details with the LuxStay team by phone, WhatsApp or e-mail so the
 * booking doesn't sit in "pending" forever.
 *
 * Props:
 *   - tone: "urgent" (red, post-booking) | "soft" (amber, listing screens)
 *   - justCreated: when true, adds a "Acabou de marcar!" headline
 *
 * The contact info is intentionally hardcoded here so it stays in sync
 * with the rest of the site (footer, WhatsApp FAB, email templates).
 */
const PHONE_PRIMARY = "967386076";          // WhatsApp + voz
const PHONE_PRIMARY_INTL = "+351 967 386 076";
const PHONE_MBWAY = "936942709";            // alternativo / MBWay
const PHONE_MBWAY_INTL = "+351 936 942 709";
const EMAIL = "luxstaymadeira@gmail.com";
const WA_LINK =
  "https://wa.me/351967386076?text=" +
  encodeURIComponent(`Olá ${brandShort()}! Acabei de marcar uma reserva e gostaria de confirmar os detalhes.`);

export default function BookingContactBadge({ tone = "urgent", justCreated = false }) {
  const urgent = tone === "urgent";
  const wrapper = urgent
    ? "border-2 border-[#dc2626] bg-[#dc2626]/10 shadow-[0_0_0_4px_rgba(220,38,38,0.08)]"
    : "border border-amber-500/50 bg-amber-500/5";
  const accent = urgent ? "text-[#fca5a5]" : "text-amber-300";
  const dot = urgent ? "bg-[#dc2626]" : "bg-amber-500";

  return (
    <div className={`relative overflow-hidden rounded-sm p-5 sm:p-6 ${wrapper}`}
         data-testid="booking-contact-badge">
      <div className="flex items-start gap-4">
        <div className={`shrink-0 w-11 h-11 rounded-full grid place-items-center ${dot}`}>
          <AlertTriangle size={22} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.25em] ${accent} font-semibold`}>
            {justCreated ? "Reserva criada · ação necessária" : "Importante"}
          </div>
          <h3 className="font-display text-2xl sm:text-3xl mt-1 leading-tight text-[#fdfbf7]">
            {justCreated
              ? `Contacte a ${brandShort()} para confirmar a sua reserva`
              : `Confirme os detalhes desta reserva com a ${brandShort()}`}
          </h3>
          <p className="text-sm text-[#fdfbf7]/90 mt-2 leading-relaxed">
            Para garantir que a sua limpeza fica agendada com sucesso, é{" "}
            <strong className="text-[#fca5a5]">essencial</strong>{" "}
            entrar em contacto connosco através de um dos meios abaixo. A equipa
            confirma a hora, a morada e a chave de acesso antes de despachar a
            equipa.
          </p>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a href={WA_LINK} target="_blank" rel="noreferrer"
               className="group flex items-center gap-3 p-3 bg-[#062c27] border border-[#1a4640] hover:border-[#25d366] hover:bg-[#25d366]/10 transition-colors"
               data-testid="contact-badge-whatsapp">
              <div className="w-10 h-10 grid place-items-center rounded-full bg-[#25d366]/15 text-[#25d366]">
                <MessageCircle size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">WhatsApp</div>
                <div className="text-sm font-semibold truncate">{PHONE_PRIMARY_INTL}</div>
              </div>
            </a>

            <a href={`tel:${PHONE_PRIMARY}`}
               className="group flex items-center gap-3 p-3 bg-[#062c27] border border-[#1a4640] hover:border-[#d4af37] hover:bg-[#d4af37]/10 transition-colors"
               data-testid="contact-badge-call">
              <div className="w-10 h-10 grid place-items-center rounded-full bg-[#d4af37]/15 text-[#d4af37]">
                <Phone size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Ligar</div>
                <div className="text-sm font-semibold truncate">{PHONE_PRIMARY_INTL}</div>
                <div className="text-[10px] text-[#a0b2af] mt-0.5">alt. {PHONE_MBWAY_INTL}</div>
              </div>
            </a>

            <a href={`mailto:${EMAIL}?subject=Confirma%C3%A7%C3%A3o%20de%20Reserva%20${encodeURIComponent(brandShort())}`}
               className="group flex items-center gap-3 p-3 bg-[#062c27] border border-[#1a4640] hover:border-[#d4af37] hover:bg-[#d4af37]/10 transition-colors"
               data-testid="contact-badge-email">
              <div className="w-10 h-10 grid place-items-center rounded-full bg-[#d4af37]/15 text-[#d4af37]">
                <Mail size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">E-mail</div>
                <div className="text-sm font-semibold truncate">{EMAIL}</div>
              </div>
            </a>
          </div>

          {urgent && (
            <div className="mt-4 text-[11px] text-[#fca5a5] uppercase tracking-widest font-semibold animate-pulse">
              ⚠ A sua reserva fica em estado pendente até confirmarmos consigo
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
